/**
 * IPO 宝库 AI 问答客户端 —— SSE 流式提问 + 历史会话读取（期 4）。
 *
 * 与会话聊天（conversationStream）同范式的独立实现：fetch + ReadableStream 手工
 * 解析 text/event-stream（需携带 JWT，不能用 EventSource），帧解析复用 `./sse`。
 * 但**不 import** 会话域的任何逻辑——宝库问答是自包含问答面（独立模块硬约束），
 * 事件契约与会话消息流（conversations 下 messages/stream）不同，两处不共享类型。
 *
 * 事件契约（POST /ipo-vault/qa/ask，Bearer 认证，Accept: text/event-stream）：
 *  - 请求体 `{"question": string, "sessionId": string|null}`
 *  - `session`   `{sessionId}` —— 后端落库的会话 id（多轮续问回传）
 *  - `delta`     `{text}`*     —— 答案增量，多次追加
 *  - `tool_use`  `{tool, action, args, callId}` —— 查询轨迹开始
 *  - `tool_result` `{tool, action, ok, resultCount, latencyMs}` —— 查询轨迹结束
 *  - `citation`  `{citations: [...]}`* —— 引用清单（可多帧追加）
 *  - `done`      `{status: ok|failed|truncated, dataAsOf, caliberNotes, runId}`
 *  - `error`     `{code, message}` —— 随后必有 done(status=failed)
 *
 * 帧字段按 camelCase 契约读取，同时兼容 snake_case 键名（`sessionId`/`session_id`
 * 等）——后端并行实现期口径可能漂移，字段读取统一走 `field()` 双 casing 兜底，
 * 避免因键名大小写让整条问答流静默丢事件。`tool_use.args` 是工具自有参数空间
 * （如 `group_by`），**不做** casing 归一，原样透传给呈现层。
 */

import { authHeaders, fetchWithAuthRetry, throwApiErrorFromResponse } from "./index";
import { readSsePostStream } from "./sse";
import type {
  VaultQaCitation,
  VaultQaRunRecord,
  VaultQaSessionSummary,
  VaultQaToolAction,
} from "../types/ipoVaultQa";

const API_PREFIX = "/api/v1/jerry";

/** 双 casing 字段读取：camelCase 优先，snake_case 兜底。 */
function field<T = unknown>(
  obj: Record<string, unknown>,
  key: string,
): T | undefined {
  if (obj[key] !== undefined) return obj[key] as T;
  const snake = key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
  return obj[snake] as T | undefined;
}

// ---------------------------------------------------------------------------
// SSE 帧类型
// ---------------------------------------------------------------------------

export interface VaultQaSessionFrame {
  sessionId: string;
}

export interface VaultQaToolUseFrame {
  tool: string;
  action: VaultQaToolAction;
  args: Record<string, unknown>;
  callId: string | null;
}

export interface VaultQaToolResultFrame {
  tool: string;
  action: VaultQaToolAction;
  ok: boolean;
  resultCount: number | null;
  latencyMs: number | null;
  /** 契约未含；真机若回传则优先按它配对轨迹。 */
  callId?: string | null;
}

export interface VaultQaDoneFrame {
  status: "ok" | "failed" | "truncated";
  dataAsOf: string | null;
  caliberNotes: string[];
  runId: string | null;
}

export interface VaultQaErrorFrame {
  code: string;
  message: string;
}

/** 宝库问答 SSE 事件（按事件类型分发，类型安全）。 */
export type VaultQaStreamEvent =
  | { type: "session"; data: VaultQaSessionFrame }
  | { type: "delta"; data: { text: string } }
  | { type: "tool_use"; data: VaultQaToolUseFrame }
  | { type: "tool_result"; data: VaultQaToolResultFrame }
  | { type: "citation"; data: { citations: VaultQaCitation[] } }
  | { type: "done"; data: VaultQaDoneFrame }
  | { type: "error"; data: VaultQaErrorFrame };

export type VaultQaStreamEventHandler = (event: VaultQaStreamEvent) => void;

// ---------------------------------------------------------------------------
// 帧归一化（双 casing → 契约类型）
// ---------------------------------------------------------------------------

function normalizeCitation(raw: Record<string, unknown>): VaultQaCitation {
  const str = (key: string): string | null => {
    const v = field<string | null>(raw, key);
    return typeof v === "string" ? v : null;
  };
  return {
    kind: str("kind") ?? "issue",
    uid: str("uid") ?? "",
    title: str("title") ?? "",
    company: str("company") ?? "",
    market: str("market"),
    board: str("board"),
    issueDate: str("issueDate"),
    deepLink: str("deepLink"),
    sourceUrl: str("sourceUrl"),
  };
}

function normalizeEvent(eventType: string, data: unknown): VaultQaStreamEvent | null {
  const raw = (data ?? {}) as Record<string, unknown>;
  switch (eventType) {
    case "session": {
      const sessionId = field<string>(raw, "sessionId");
      return sessionId ? { type: "session", data: { sessionId } } : null;
    }
    case "delta": {
      const text = field<string>(raw, "text");
      return text != null ? { type: "delta", data: { text } } : null;
    }
    case "tool_use":
      return {
        type: "tool_use",
        data: {
          tool: field<string>(raw, "tool") ?? "",
          action: field<string>(raw, "action") ?? "",
          args: (field<Record<string, unknown>>(raw, "args") ?? {}) as Record<string, unknown>,
          callId: field<string>(raw, "callId") ?? null,
        },
      };
    case "tool_result": {
      const ok = field<boolean>(raw, "ok");
      const rc = field<number>(raw, "resultCount");
      const lm = field<number>(raw, "latencyMs");
      const cid = field<string>(raw, "callId");
      return {
        type: "tool_result",
        data: {
          tool: field<string>(raw, "tool") ?? "",
          action: field<string>(raw, "action") ?? "",
          ok: ok == null ? true : ok,
          resultCount: typeof rc === "number" ? rc : null,
          latencyMs: typeof lm === "number" ? lm : null,
          callId: typeof cid === "string" ? cid : null,
        },
      };
    }
    case "citation": {
      const list = field<unknown[]>(raw, "citations");
      return {
        type: "citation",
        data: {
          citations: Array.isArray(list)
            ? list.map((c) => normalizeCitation((c ?? {}) as Record<string, unknown>))
            : [],
        },
      };
    }
    case "done": {
      const notes = field<unknown[]>(raw, "caliberNotes");
      return {
        type: "done",
        data: {
          status: (field<string>(raw, "status") ?? "ok") as VaultQaDoneFrame["status"],
          dataAsOf: field<string>(raw, "dataAsOf") ?? null,
          caliberNotes: Array.isArray(notes)
            ? notes.filter((n): n is string => typeof n === "string")
            : [],
          runId: field<string>(raw, "runId") ?? null,
        },
      };
    }
    case "error":
      return {
        type: "error",
        data: {
          code: field<string>(raw, "code") ?? "UNKNOWN",
          message: field<string>(raw, "message") ?? "问答失败",
        },
      };
    default:
      return null; // 未知事件：丢弃，不中断整条流。
  }
}

// ---------------------------------------------------------------------------
// SSE 提交流
// ---------------------------------------------------------------------------

/**
 * 提问并流式消费回答。
 *
 * - 流开始前失败（4xx/5xx JSON 错误信封）抛 `ApiError`（401 触发统一登出）。
 * - 流中途网络断开抛原生错误；调用方按「本轮中断」处理并给出重试入口。
 * - `signal` 中止抛 DOMException(AbortError)，用于组件卸载清理。
 * - resolve 于解析到 `done` 帧（readSsePostStream 在终态后主动收敛）。
 */
export function streamVaultQaAsk(
  body: { question: string; sessionId: string | null },
  onEvent: VaultQaStreamEventHandler,
  signal?: AbortSignal,
): Promise<void> {
  return (async () => {
    const res = await fetchWithAuthRetry(`${API_PREFIX}/ipo-vault/qa/ask`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        ...authHeaders(),
      },
      body: JSON.stringify({
        question: body.question,
        sessionId: body.sessionId ?? null,
      }),
      signal,
    });

    if (!res.ok) {
      await throwApiErrorFromResponse(res, { method: "POST", url: "/ipo-vault/qa/ask" });
    }

    await readSsePostStream(res, (eventType, data) => {
      const event = normalizeEvent(eventType, data);
      if (event) onEvent(event);
    });
  })();
}

// ---------------------------------------------------------------------------
// 历史会话（GET）
// ---------------------------------------------------------------------------

/**
 * 兼容 `{success, data}` 信封与裸数组两种线格式——并行实现期的契约按裸数组
 * 冻结，但 jerry 网关绝大多数端点走信封；两种都接，落地以真机联调为准。
 */
function unwrapList<T>(res: unknown): T[] {
  if (Array.isArray(res)) return res as T[];
  const envelope = res as { data?: unknown } | null;
  if (envelope && typeof envelope === "object" && Array.isArray(envelope.data)) {
    return envelope.data as T[];
  }
  return [];
}

export const vaultQaApi = {
  /** 本人问答会话列表（按创建时间倒序）。 */
  sessions: async (): Promise<VaultQaSessionSummary[]> => {
    const res = await fetchWithAuthRetry(`${API_PREFIX}/ipo-vault/qa/sessions`, {
      headers: { ...authHeaders() },
    });
    if (!res.ok) {
      await throwApiErrorFromResponse(res, { method: "GET", url: "/ipo-vault/qa/sessions" });
    }
    return unwrapList<Record<string, unknown>>(await res.json()).map((raw) => ({
      id: field<string>(raw, "id") ?? "",
      title: field<string>(raw, "title") ?? null,
      messageCount: field<number>(raw, "messageCount") ?? 0,
      lastQuestion: field<string>(raw, "lastQuestion") ?? null,
      createdAt: field<string>(raw, "createdAt") ?? "",
    }));
  },

  /** 单会话的全部问答轮（只读回放）。 */
  sessionRuns: async (sessionId: string): Promise<VaultQaRunRecord[]> => {
    const res = await fetchWithAuthRetry(
      `${API_PREFIX}/ipo-vault/qa/sessions/${encodeURIComponent(sessionId)}/runs`,
      { headers: { ...authHeaders() } },
    );
    if (!res.ok) {
      await throwApiErrorFromResponse(res, {
        method: "GET",
        url: `/ipo-vault/qa/sessions/${sessionId}/runs`,
      });
    }
    return unwrapList<Record<string, unknown>>(await res.json()).map((raw) => {
      const citations = field<unknown[]>(raw, "citations") ?? [];
      const notes = field<unknown[]>(raw, "caliberNotes") ?? [];
      return {
        id: field<string>(raw, "id") ?? "",
        question: field<string>(raw, "question") ?? "",
        answerText: field<string>(raw, "answerText") ?? "",
        status: field<string>(raw, "status") ?? "ok",
        citations: Array.isArray(citations)
          ? citations.map((c) => normalizeCitation((c ?? {}) as Record<string, unknown>))
          : [],
        caliberNotes: notes.filter((n): n is string => typeof n === "string"),
        dataAsOf: field<string>(raw, "dataAsOf") ?? null,
        createdAt: field<string>(raw, "createdAt") ?? "",
      };
    });
  },
};
